import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '@/components/ui/Avatar';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { Menu } from '@/components/ui/Menu';
import { SearchInput } from '@/components/ui/SearchInput';
import { PERMISSIONS } from '@/constants/permissions';
import { buildRoute, ROUTES, SEARCH_PARAMS } from '@/constants/routes';
import { accountName, useAuthUser, useLogout, usePermissions } from '@/features/auth';

import styles from './TopBar.module.css';

export interface TopBarProps {
  onOpenMenu: () => void;
}

/**
 * Menu button (narrow screens), member search and the account menu. Theme lives only in
 * Settings, by the owner's choice — the client keeps the app light.
 */
export function TopBar({ onOpenMenu }: TopBarProps) {
  const { can } = usePermissions();

  return (
    <header className={styles.bar}>
      <IconButton
        icon="menu"
        label="Open menu"
        variant="ghost"
        className={styles.menuButton}
        onClick={onOpenMenu}
      />
      <span className={styles.compactBrand}>
        <BrandLogo size="sm" badgeOnly />
      </span>

      {can(PERMISSIONS.MEMBERS_READ) && <MemberSearch />}

      <div className={styles.actions}>
        <AccountMenu />
      </div>
    </header>
  );
}

/** Jumps to the members list with the term in `?q=`, where the list's own search box shows it. */
function MemberSearch() {
  const navigate = useNavigate();
  const [text, setText] = useState('');

  return (
    <form
      role="search"
      className={styles.search}
      onSubmit={(event) => {
        event.preventDefault();
        const query = text.trim();
        void navigate(
          query
            ? buildRoute.withQuery(ROUTES.MEMBERS, { [SEARCH_PARAMS.QUERY]: query })
            : ROUTES.MEMBERS,
        );
        setText('');
      }}
    >
      <SearchInput
        label="Search members"
        placeholder="Find a member — name, mobile or code"
        value={text}
        onChange={setText}
        enterKeyHint="search"
      />
    </form>
  );
}

function AccountMenu() {
  const user = useAuthUser();
  const navigate = useNavigate();
  const logout = useLogout();
  const name = accountName(user);

  return (
    <Menu
      label="Account menu"
      triggerClassName={styles.account}
      trigger={
        <>
          <Avatar name={name} size="md" />
          <span className={styles.accountText}>
            <span className={styles.accountName}>{name}</span>
            <span className={styles.accountRole}>{user.role.name}</span>
          </span>
          <Icon name="chevron-down" size={16} className={styles.chevron} />
        </>
      }
      items={[
        {
          label: 'Settings',
          icon: 'settings',
          onSelect: () => {
            void navigate(ROUTES.SETTINGS);
          },
        },
        {
          label: 'Change password',
          icon: 'key',
          onSelect: () => {
            void navigate(ROUTES.CHANGE_PASSWORD);
          },
        },
        {
          label: 'Log out',
          icon: 'logout',
          tone: 'danger',
          disabled: logout.isPending,
          onSelect: () => {
            logout.mutate();
          },
        },
      ]}
    />
  );
}
